import { cloneElement, forwardRef, useId, useState } from 'react';

/**
 * A supplementary hint bound to a single focusable trigger element
 * (spec §7.1, §12). Shows on hover AND keyboard focus, hides on Escape (or on
 * blur/mouseleave). The trigger must already carry its own accessible name —
 * Tooltip is never the only carrier of information, only a wired-up
 * `aria-describedby` addition on top of it.
 *
 * @param {object} props
 * @param {import('react').ReactNode} props.content - tooltip copy.
 * @param {import('react').ReactElement} props.children - a single focusable
 *   element (button, link, etc.) that receives the hover/focus handlers and
 *   `aria-describedby`.
 * @param {'top'|'bottom'|'left'|'right'} [props.placement='top']
 * @param {string} [props.className] - applied to the `.etch-tooltip` bubble.
 */
export const Tooltip = forwardRef(function Tooltip(
  { content, children, placement = 'top', className = '', ...rest },
  ref
) {
  const [visible, setVisible] = useState(false);
  const tooltipId = useId();

  const show = () => setVisible(true);
  const hide = () => setVisible(false);

  const trigger = cloneElement(children, {
    'aria-describedby': content ? tooltipId : children.props['aria-describedby'],
    onMouseEnter: (event) => {
      show();
      children.props.onMouseEnter?.(event);
    },
    onMouseLeave: (event) => {
      hide();
      children.props.onMouseLeave?.(event);
    },
    onFocus: (event) => {
      show();
      children.props.onFocus?.(event);
    },
    onBlur: (event) => {
      hide();
      children.props.onBlur?.(event);
    },
    onKeyDown: (event) => {
      if (event.key === 'Escape') hide();
      children.props.onKeyDown?.(event);
    }
  });

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }} {...rest}>
      {trigger}
      {visible && content && (
        <span role="tooltip" id={tooltipId} data-placement={placement} className={`etch-tooltip ${className}`.trim()}>
          {content}
        </span>
      )}
    </span>
  );
});

export default Tooltip;
